import { useEffect, useRef, useCallback } from 'react';
import { getCookie, TOKEN_COOKIE } from '../utils/cookie-service';

const POLL_INTERVAL_MS = 2500; // 2.5 seconds

/**
 * useSessionSync — Cross-tab session synchronization hook.
 *
 * Polls the veriipro-token cookie to detect login/logout events from other tabs/portals.
 * Also uses visibilitychange event for immediate re-check when tab becomes active.
 *
 * @param {Object} options
 * @param {Function} options.onLogin - Called when token appears (user logged in from another tab)
 * @param {Function} options.onLogout - Called when token disappears (user logged out from another tab)
 * @param {boolean} [options.enabled=true] - Whether polling is active
 * @returns {{ isLoggedIn: boolean }}
 */
export function useSessionSync({ onLogin, onLogout, enabled = true } = {}) {
  const tokenRef = useRef(!!getCookie(TOKEN_COOKIE));
  const intervalRef = useRef(null);
  const onLoginRef = useRef(onLogin);
  const onLogoutRef = useRef(onLogout);

  // Keep callback refs fresh without re-creating the interval
  useEffect(() => {
    onLoginRef.current = onLogin;
  }, [onLogin]);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const checkToken = useCallback(() => {
    const currentToken = !!getCookie(TOKEN_COOKIE);
    const previousToken = tokenRef.current;

    if (currentToken && !previousToken) {
      // Token appeared — user logged in from another tab
      tokenRef.current = true;
      if (onLoginRef.current) {
        onLoginRef.current();
      }
    } else if (!currentToken && previousToken) {
      // Token disappeared — user logged out from another tab
      tokenRef.current = false;
      if (onLogoutRef.current) {
        onLogoutRef.current();
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Start polling interval
    intervalRef.current = setInterval(checkToken, POLL_INTERVAL_MS);

    // Also check on visibility change (handles "user logs in on another tab, returns to this tab")
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to allow cookie propagation
        setTimeout(checkToken, 300);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkToken]);

  return { isLoggedIn: tokenRef.current };
}

export default useSessionSync;
